import { normalizeAssistantText } from '@/lib/assistant/assistantTextUtils';
import {
  conversationalComplaintLevel,
  complaintFallbackKeys,
  detectDeliveryAnxiety,
  extractGreetingAndRemainder,
  isFarewellMessage,
  isPureHowAreYou,
  shortGreetingLine,
} from '@/lib/assistant/assistantConversation';

/**
 * Cuando el flujo está en “¿Pa cuántos es?” — texto libre equivalente a los botones.
 */
export function matchOrderStepFreeText(n, trimmed) {
  if (trimmed.length > 48) return null;
  if (
    /\b(horario|cuanto|cuánto|cuesta|delivery|domicilio|retiro|menu|menú|promo|oferta|whatsapp|gracias|chau|hola|buenas|lleva|ingredientes|vegan|vegetar|gluten)\b/.test(
      n
    )
  ) {
    return null;
  }

  if (/\b(barato|barata|economico|económico|mas\s+barato|más\s+barato|algo\s+barato)\b/.test(n)) {
    return { choice: 'cheap', ack: '¡Dale! Te muestro lo más accesible 👇' };
  }
  if (/\b(sorpr[eé]ndeme|sorprende|sorpresa)\b/.test(n)) {
    return { choice: 'surprise', ack: '¡Buena! Te sorprendo con algo del menú ✨' };
  }
  if (/\b(piola|algo\s+piola|especial)\b/.test(n)) {
    return { choice: 'special', ack: '¡Dale! Mirá estas opciones destacadas ⭐' };
  }
  if (/\b(categor[ií]a|categor[ií]as|por\s+categor)\b/.test(n)) {
    return { choice: 'categories', ack: 'Perfecto, vamos por categorías 📋' };
  }

  if (
    /\b(somos\s+varios|familia|un\s+grupo|grupo|much[oa]s|varias\s+personas|varios\s+personas)\b/.test(n) ||
    /\b(3\s+o\s+m[aá]s|cuatro|cuatro\s+personas|cinco)\b/.test(n)
  ) {
    return { choice: 'group', ack: '¡Listo! Opciones para el grupo 👨‍👩‍👧‍👦' };
  }
  if (
    /\b(para\s+2|pa\s+2|somos\s+2|dos\s+personas|en\s+pareja|los\s+dos|pareja)\b/.test(n) ||
    /^(2|dos)\s*$/i.test(trimmed)
  ) {
    return { choice: 'duo', ack: '¡Dale! Opciones para 2 👥' };
  }
  if (
    /\b(para\s+mi|pa\s+mi|yo\s+solo|solo\s+yo|nom[aá]s|nomas|pa\s+uno|para\s+uno|pa\s+m[ií]\s+nom[aá]s)\b/.test(
      n
    ) ||
    /^(1|uno)\s*$/i.test(trimmed)
  ) {
    return { choice: 'solo', ack: '¡Buenísimo! Ideas pa uno 👤' };
  }

  return null;
}

/**
 * Saludos, despedidas y capa social antes de intents de producto/delivery.
 * @param {function(string, object): object} resolveIntent — buildFreeTextAnswer sin capa social
 */
export function trySocialLayer(menuData, trimmed, n, defaultFallback, layerOptions = {}, resolveIntent) {
  if (isFarewellMessage(n, trimmed)) {
    const picks = [
      '¡Gracias a ti! 🙌 Que disfrutes tu pedido 😄',
      '¡Que te vaya súper! 👋',
      '¡Nos vemos! 😄',
    ];
    return {
      text: picks[trimmed.length % picks.length],
      products: null,
      fallbackKeys: defaultFallback,
    };
  }

  const { hadGreeting, remainder } = extractGreetingAndRemainder(trimmed);
  const remNorm = normalizeAssistantText(remainder);
  const remTrim = remainder.trim();

  if (hadGreeting && remTrim.length >= 2) {
    if (isPureHowAreYou(remNorm, remTrim)) {
      return {
        text: '¡Hola! 👋 ¡Bien, gracias! 😄 ¿Y tú? ¿Qué te gustaría pedir hoy?',
        products: null,
        fallbackKeys: defaultFallback,
      };
    }

    const cRem = conversationalComplaintLevel(remNorm);
    if (cRem > 0) {
      const gl = shortGreetingLine(n).replace(/👋$/, '').trim();
      const body =
        cRem >= 2
          ? 'Pucha, lamento que te haya pasado eso. Quiero ayudarte a resolverlo lo antes posible.'
          : 'Pucha, lamento que te haya pasado eso. Quiero ayudarte a que esta vez sea mejor.';
      return {
        text: `${gl} 👋 ${body}`,
        products: null,
        fallbackKeys: complaintFallbackKeys(menuData?.business || {}, cRem),
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }

    if (detectDeliveryAnxiety(remNorm)) {
      const gl = shortGreetingLine(n).replace(/👋$/, '').trim();
      return {
        text: `${gl} 👋 Perfecto: al momento de pedir vas a poder ver el tiempo estimado según tu dirección antes de confirmar.`,
        products: null,
        fallbackKeys: ['order_hint', 'catalog'],
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }

    const orderOnly =
      remTrim.length > 0 && matchOrderStepFreeText(remNorm, remTrim) !== null;
    const intentPart = resolveIntent(remainder, {
      skipSocialLayer: true,
      skipConversationalLayer: true,
      flowContext: layerOptions.flowContext,
      treatAsOrderPartyStep: orderOnly,
    });
    return {
      text: `${shortGreetingLine(n)}`,
      products: null,
      fallbackKeys: defaultFallback,
      followUp: intentPart,
    };
  }

  if (hadGreeting) {
    const soloQueTal = /\b(qu[eé]|q)\s+tal\b/.test(n) && !/\bhola\b/.test(n);
    return {
      text: soloQueTal
        ? '¡Qué tal! 👋 ¿Qué te gustaría pedir hoy?'
        : '¡Hola! 👋 ¿Cómo estás? ¿Qué te gustaría pedir hoy?',
      products: null,
      fallbackKeys: defaultFallback,
    };
  }

  if (isPureHowAreYou(n, trimmed)) {
    return {
      text: '¡Bien, gracias! 😄 ¿Y tú? ¿Qué te gustaría pedir hoy?',
      products: null,
      fallbackKeys: defaultFallback,
    };
  }

  return null;
}
